import React, { useState } from "react";
import { Moon, Sparkle, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * The Celestial Card — a premium design that automatically displays a recipient's
 * constellation + zodiac traits in Maison Gold, with an optional AI-Generated
 * Astro-Sentiment (an extension of the Magic Wand): a bespoke insight blending the
 * recipient's sign with the CURRENT live zodiac season, in the Sovereign brand voice.
 *
 * Zero-Gap Consistency: same gold gradient (#f6e3a3 -> #c9a24a -> #8a6a2a) and
 * Playfair Display italic serif used by FounderBadge/SealBadge, so it reads as part of
 * the same visual language.
 */
const CelestialCard = ({ zodiac, lovedOneId, size = "md", showInsight = true, className = "" }) => {
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!zodiac) return null;
  const compact = size === "sm";

  const revealInsight = async () => {
    if (!lovedOneId) return;
    setLoading(true); setError("");
    try {
      const res = await api.post(`/loved-ones/${lovedOneId}/celestial-insight`);
      setInsight(res.data.insight);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || "Could not channel an insight right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="celestial-card"
      className={`relative rounded-2xl border overflow-hidden ${compact ? "p-3" : "p-5"} ${className}`}
      style={{
        background: "linear-gradient(135deg, rgba(246,227,163,0.18) 0%, rgba(201,162,74,0.14) 50%, rgba(138,106,42,0.18) 100%)",
        borderColor: "#c9a24a",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex items-center justify-center rounded-full shrink-0 ${compact ? "w-10 h-10 text-xl" : "w-14 h-14 text-2xl"}`}
          style={{ background: "linear-gradient(135deg, #f6e3a3 0%, #c9a24a 50%, #8a6a2a 100%)", color: "#3d2e0f" }}
        >
          {zodiac.symbol}
        </div>
        <div>
          <div
            className={`italic font-semibold ${compact ? "text-base" : "text-xl"}`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#8a6a2a" }}
            data-testid="celestial-sign"
          >
            {zodiac.sign}
          </div>
          <div className="text-xs text-muted-foreground capitalize">{zodiac.element} sign · {zodiac.traits}</div>
        </div>
      </div>

      {showInsight && (
        <div className="mt-3">
          {insight ? (
            <p className="text-sm font-serif italic text-foreground/90" data-testid="celestial-insight-text">&ldquo;{insight}&rdquo;</p>
          ) : (
            <button type="button" data-testid="celestial-insight-btn" onClick={revealInsight} disabled={loading || !lovedOneId}
              className="text-xs flex items-center gap-1.5 gold-text hover:underline disabled:opacity-50">
              {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Channeling the stars…</> : <><Sparkle className="w-3.5 h-3.5" /> Reveal this season&apos;s insight</>}
            </button>
          )}
          {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
        </div>
      )}
    </div>
  );
};

export const CelestialSymbol = ({ zodiac, className = "" }) => (
  zodiac ? <span className={`inline-flex items-center gap-1 text-xs gold-text ${className}`} title={zodiac.sign}><Moon className="w-3 h-3" /> {zodiac.sign} {zodiac.symbol}</span> : null
);

export default CelestialCard;
